import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { FormatService } from '@/app/common/format/format.service';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { MagicSearchService } from '@/app/pages/creators/service/magic-search/magic-search.service';
import { CreatorService } from '@/app/services/creator/creator.service';
import { ImageService } from '@/app/services/image/image.service';
import { MiscService } from '@/app/services/misc/misc.service';
import { take } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { differenceInDays } from 'date-fns';
import { DecimalPipe } from '@angular/common';
import { CollaboratorService } from '@/app/services/collaborator/collaborator.service';
import { normalizeReviewNote, REVIEW_NOTE_MAX_LENGTH } from '@/app/common/general-types/recruit-groups.review';

@Component({
  selector: 'cmg-grou-creator-card',
  imports: [LdgImgComponent, DecimalPipe],
  templateUrl: './grou-creator-card.html',
  styleUrl: './grou-creator-card.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrouCreatorCard {
  readonly creator = input.required<CreatorResponseDto>();
  readonly navCreators = input<CreatorResponseDto[]>([]);
  readonly actions = input<'invite' | 'review' | 'none'>('invite');
  readonly declined = input(false);
  readonly approved = input(false);
  readonly invited = input(false);
  readonly reviewLabel = input<string | undefined>(undefined);
  readonly reviewNote = input<string | undefined>(undefined);
  readonly noteEditable = input(false);
  readonly approve = output<void>();
  readonly decline = output<void>();
  readonly noteChange = output<string>();
  readonly remove = output<void>();

  readonly feedbackOnly = computed(() =>
    !!this.reviewNote() && !this.approved() && !this.declined(),
  );

  readonly reviewLabelTextClass = computed(() => {
    if (this.declined()) {
      return 'text-rose-700 dark:text-rose-300';
    }
    if (this.approved()) {
      return 'text-green-700 dark:text-green-300';
    }
    if (this.feedbackOnly()) {
      return 'text-sky-700 dark:text-sky-300';
    }
    return 'text-secondary';
  });

  today: Date = new Date();

  /** Services */
  private readonly formatService = inject(FormatService);
  private readonly imageService = inject(ImageService);
  private readonly miscService = inject(MiscService);
  public collaboratorsService = inject(CollaboratorService);
  private readonly creatorService = inject(CreatorService);
  private readonly magicSearchService = inject(MagicSearchService);
  /** Services - END */

  readonly hometown = computed(() =>
    this.miscService.getShortHometown(this.creator().homeTownName),
  );

  readonly ageYears = computed(() =>
    this.formatService.calculateAge(this.creator().dateOfBirth),
  );

  readonly displayName = computed(() => {
    const c = this.creator();
    const lastPart = c.lastName ? `${c.lastName.charAt(0)}.` : '';
    return `${c.firstName} ${lastPart}`.trim();
  });

  readonly removeAriaLabel = computed(() => `Remove ${this.displayName()} from group`);

  readonly tiktokFollowersLabel = computed(() => {
    const n = this.creator().tikTokFollowers;
    return n > 0 ? this.formatService.convertIntToString(n) : '—';
  });

  readonly instaFollowersLabel = computed(() => {
    const n = this.creator().instaFollowers;
    return n > 0 ? this.formatService.convertIntToString(n) : '—';
  });

  readonly snapFollowersLabel = computed(() => {
    const n = this.creator().snapchatFollowers;
    return n > 0 ? this.formatService.convertIntToString(n) : '—';
  });

  readonly ratingStatusLabel = computed(() => {
    const c = this.creator();
    const avg = c.reviews_summary?.average_rating ?? c.rating;
    if (avg > 0) {
      return `${avg.toFixed(1)} rating`;
    }
    return 'Unrated';
  });

  readonly ratingStatusDescribed = computed(() => {
    const c = this.creator();
    const avg = c.reviews_summary?.average_rating ?? c.rating;
    const total = c.reviews_summary?.total_reviews ?? 0;
    if (avg > 0 && total > 0) {
      return `Average rating ${avg.toFixed(1)} from ${total} reviews`;
    }
    if (avg > 0) {
      return `Average rating ${avg.toFixed(1)}`;
    }
    return 'This creator has not been rated yet';
  });

  readonly createdDaysAgo = computed(() => {
    return differenceInDays(this.today, this.creator().createdDate);
  });

  readonly noteFieldId = computed(() => `creator-note-${this.creator().blazeFirebaseUid}`);
  readonly noteMaxLength = REVIEW_NOTE_MAX_LENGTH;

  imageSource(): string {
    return this.imageService.getImageSource(this.creator().photoURL, 'gallery_2xl');
  }

  getFlag() {
    switch (this.creator() && this.creator().countryCode) {
      case 'GB':
        return '🇬🇧';
      case 'US':
        return '🇺🇸';
      default:
        return '🇬🇧';
    }
  }

  launchPortfolio() {
    const creator = this.creator();
    this.magicSearchService.openCreatorPortfolio(creator, {
      navItems: this.navCreators().map((item) => ({ creator: item })),
    });
    if (creator.ssCreator) {
      return;
    }

    this.creatorService.getCreatorById(creator.blazeFirebaseUid).pipe(take(1)).subscribe({
      next: (fullCreator) => {
        if (this.magicSearchService.portfolioCreatorId() === fullCreator.blazeFirebaseUid) {
          this.magicSearchService.portfolioCreator.set(fullCreator);
        }
      },
      error: (err) => {
        console.warn('Unable to load creator socials for group profile: ', err);
      },
    });
  }

  inviteToBrief(event: Event): void {
    this.collaboratorsService.selectCreator(this.creator());
    const button = event.currentTarget;
    if (button instanceof HTMLElement) {
      this.collaboratorsService.triggerSelectionGuide(button.getBoundingClientRect());
    }
  }

  stopCardClick(event: Event): void {
    event.stopPropagation();
  }

  onNoteBlur(event: Event): void {
    this.emitNote((event.target as HTMLTextAreaElement).value);
  }

  onNoteEnter(event: Event): void {
    event.preventDefault();
    this.emitNote((event.target as HTMLTextAreaElement).value);
    (event.target as HTMLTextAreaElement).blur();
  }

  private emitNote(value: string): void {
    const note = normalizeReviewNote(value);
    if (note === normalizeReviewNote(this.reviewNote())) {
      return;
    }
    this.noteChange.emit(note);
  }

}
